import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import Footer from "@/components/Footer";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import Seo, { composeTitle, truncateForMeta } from "@/components/Seo";
import { Container, SectionLabel, ButtonLux } from "@/components/primitives";
import { useSettings } from "@/context/SettingsContext";
import { api, mediaSrc } from "@/lib/api";

function ItemGrid({ items, onEnquire, prefix }) {
  return (
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-8">
      {items.map((it, i) => {
        const img = it.image_url ? mediaSrc(it.image_url) : "";
        return (
          <div key={`${prefix}-${i}`} data-testid={`${prefix}-${i}`} className="bg-lux-surface">
            {img ? <img src={img} alt={it.title} className="w-full h-80 object-cover" /> : <div className="w-full h-80 bg-[#EAE3D8]" />}
            <div className="p-5 flex flex-col gap-2">
              <h3 className="font-serif text-xl text-lux-ink">{it.title}</h3>
              {it.tagline ? <p className="text-[13px] text-lux-sec leading-relaxed font-light">{it.tagline}</p> : null}
              <div className="mt-2.5 flex items-center justify-between border-t border-lux-line pt-3">
                <span className="text-[11px] tracking-[0.2em] uppercase text-lux-muted">{it.price_note || "On enquiry"}</span>
                <button type="button" onClick={() => onEnquire(it.title)} data-testid={`${prefix}-${i}-enquire`} className="text-[11px] tracking-[0.25em] text-lux-accent hover:text-lux-ink transition-colors">ENQUIRE →</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function GiftingCategory() {
  const { category } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [doc, setDoc] = useState(null);
  useEffect(() => { api.getPage("gifting").then(setDoc).catch(() => {}); }, []);

  const cat = useMemo(() => {
    const list = doc?.content?.categories?.items || [];
    return list.find((c) => c && c.slug === category) || null;
  }, [doc, category]);

  useEffect(() => { if (cat && cat.slug === "curated") navigate("/gifting/curated", { replace: true }); }, [cat, navigate]);

  const enquire = (title) => navigate(`/gifting/curated?item=${encodeURIComponent(title)}`);

  if (!doc) return (<div className="min-h-screen bg-lux-bg"><TopBar /><Container className="pt-40 pb-24"><p className="text-lux-muted">Loading…</p></Container></div>);

  if (!cat) {
    return (
      <div className="min-h-screen bg-lux-bg">
        <TopBar />
        <Container className="pt-40 pb-24">
          <h1 className="font-serif text-4xl text-lux-ink font-light">Category not found.</h1>
          <div className="mt-6"><ButtonLux label="Back to Gifting" variant="outline" to="/gifting" testId="back-to-gifting" /></div>
        </Container>
        <Footer />
      </div>
    );
  }

  const items = (cat.items || []).filter((it) => it && it.title);
  const edits = (cat.edits || []).filter((e) => e && e.visible !== false && e.title);
  const hasContent = items.length + edits.length > 0;

  return (
    <div className="min-h-screen bg-lux-bg">
      <Seo
        title={composeTitle(cat.title, settings.seo.default_title, settings.seo.title_suffix)}
        description={truncateForMeta(cat.description || cat.tagline || settings.seo.default_description)}
        path={`/gifting/${cat.slug}`}
        imageUrl={cat.image_url ? mediaSrc(cat.image_url) : undefined}
      />
      <TopBar />
      <Container className="pt-32 md:pt-40 pb-24">
        <Link to="/gifting" data-testid="cat-breadcrumb" className="text-[11px] tracking-[0.25em] text-lux-muted hover:text-lux-ink transition-colors">← GIFTING</Link>
        <div className="mt-6">
          <SectionLabel>{`· ${cat.index || ""} ·`}</SectionLabel>
          <h1 className="font-serif text-lux-ink font-light tracking-tight leading-none mt-3 text-4xl md:text-6xl">{cat.title}</h1>
          {cat.tagline ? <p className="text-[15px] text-lux-sec italic mt-3">{cat.tagline}</p> : null}
          {cat.description ? <p className="text-base md:text-[17px] text-lux-sec font-light leading-relaxed mt-5 max-w-[720px]">{cat.description}</p> : null}
        </div>

        <div className="h-12" />

        {edits.length > 0 && (
          <div className="flex flex-col gap-16">
            {edits.map((edit) => (
              <div key={edit.slug || edit.title} data-testid={`edit-${edit.slug}`}>
                <SectionLabel>· Seasonal Edit ·</SectionLabel>
                <h2 className="font-serif text-2xl md:text-3xl text-lux-ink font-light tracking-tight mt-2">{edit.title}</h2>
                {edit.tagline ? <p className="text-sm text-lux-sec italic mt-1.5 mb-5">{edit.tagline}</p> : <div className="h-5" />}
                <ItemGrid items={edit.items || []} onEnquire={enquire} prefix={`edit-${edit.slug}-item`} />
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className={edits.length > 0 ? "mt-16" : ""}>
            {edits.length > 0 && <><SectionLabel>{`· More from ${cat.title} ·`}</SectionLabel><div className="h-5" /></>}
            <ItemGrid items={items} onEnquire={enquire} prefix={`cat-${cat.slug}-item`} />
          </div>
        )}

        {!hasContent && (
          <div className="py-16 flex flex-col items-start">
            <SectionLabel>· A New Edit Is Being Composed ·</SectionLabel>
            <h2 className="font-serif text-2xl md:text-3xl text-lux-ink font-light mt-3">This edit is being assembled with care.</h2>
            <p className="text-[15px] text-lux-sec font-light leading-relaxed max-w-[620px] mt-4">
              While we compose the next selection for this category, share who you’re gifting and the occasion — we’ll curate for you directly.
            </p>
            <div className="mt-6"><ButtonLux label="Start a Curated Gift" to={`/gifting/curated?item=${encodeURIComponent(cat.title)}`} testId="empty-curated-cta" /></div>
          </div>
        )}

        {hasContent && (
          <div className="mt-24 pt-16 border-t border-lux-line flex flex-col items-start">
            <SectionLabel>· Prefer Something Bespoke? ·</SectionLabel>
            <h2 className="font-serif text-xl md:text-2xl text-lux-ink font-light mt-3">We can curate a Gift around the person, not the catalogue.</h2>
            <div className="mt-5"><ButtonLux label="Start a Curated Gift" variant="outline" to="/gifting/curated" testId="cat-curated-cta" /></div>
          </div>
        )}
      </Container>
      <Footer />
      <WhatsAppFAB />
    </div>
  );
}
